import { useCallback, useMemo, useSyncExternalStore } from "react";
import { CONSULTA_SHELL, CONSULTA_PAGINA } from "../ui/breakpoints";

/**
 * =============================================================================
 * DETECCION DE VIEWPORT — SIEMPRE EN POSITIVO
 * =============================================================================
 * SE PREGUNTA "¿ES ESTRECHO?", NUNCA "¿NO ES ANCHO?". No es estilo: es la
 * diferencia entre una suite verde y una suite que pinta otra cosa sin avisar.
 *
 * `setupTests.js` sustituye window.matchMedia por un doble que devuelve
 * `matches: false` para CUALQUIER consulta. Preguntando en positivo, eso
 * significa ESCRITORIO en los tests, que es lo que asumen todas las suites.
 * Preguntando en negativo (`!screens.lg`) significaria MOVIL en los 867 a la
 * vez, y no fallaria ninguno: simplemente pintarian otra rama. Es el fallo mas
 * caro posible, porque no avisa.
 *
 * Tampoco uses Grid.useBreakpoint() de antd para esto: bajo el mismo doble
 * devuelve todos los tramos en falso, con lo que la direccion de la
 * comparacion decide el resultado. Este hook no deja esa eleccion abierta.
 *
 * NO SE REEXPORTA desde `hooks/index.js`: hay 37 suites que sustituyen ese
 * barril entero con jest.mock, y el hook desapareceria en todas ellas. Se
 * importa siempre por su ruta.
 * =============================================================================
 */
function useConsultaMedia(consulta) {
  const suscribir = useCallback(
    (alCambiar) => {
      if (typeof window === "undefined" || !window.matchMedia) {
        return () => {};
      }
      const mql = window.matchMedia(consulta);
      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", alCambiar);
        return () => mql.removeEventListener("change", alCambiar);
      }
      // Safari antiguo
      if (typeof mql.addListener === "function") {
        mql.addListener(alCambiar);
        return () => mql.removeListener(alCambiar);
      }
      return () => {};
    },
    [consulta],
  );

  const leer = useCallback(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(consulta).matches;
  }, [consulta]);

  // El tercer argumento es el valor del servidor. No hay SSR en este
  // workspace, pero si lo hubiera el valor seguro sigue siendo escritorio.
  return useSyncExternalStore(suscribir, leer, () => false);
}

/** true cuando el menu debe ser un cajon en vez de una columna (< 992 px). */
export function useShellEstrecho() {
  return useConsultaMedia(CONSULTA_SHELL);
}

/** true cuando el contenido debe reorganizarse en una sola columna (< 768 px). */
export function useMovil() {
  return useConsultaMedia(CONSULTA_PAGINA);
}

/** Los dos a la vez, para quien necesita distinguir las dos fronteras. */
export function useAnchoDePantalla() {
  const shellEstrecho = useShellEstrecho();
  const movil = useMovil();
  return useMemo(() => ({ shellEstrecho, movil }), [shellEstrecho, movil]);
}
